import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';
import {
  buildCountryTitleListParams,
  normalizeCountryTitleStatus,
  toCountryTitleApiPayload,
} from '../../../utils/country-title-catalog.utils';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Regímenes por país.
 * Endpoint: `/settings/countries/{countryId}/regimens`
 */
@Injectable({ providedIn: 'root' })
export class RegimeService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private resource(countryId: string, id?: string | number): string {
    const baseUrl = `${this.apiUrl}/${countryId}/regimens`;
    return id !== undefined && id !== null ? `${baseUrl}/${id}` : baseUrl;
  }

  private mapItem(it: any): Regime {
    return {
      id: it.id,
      name: it.name || it.title || '',
      relations: it.relations || 0,
      createdAt: it.createdAt || it.created_at || '',
      status: normalizeCountryTitleStatus(it.status),
    };
  }

  getRegimes(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<RegimePageResponse> {
    const params = buildCountryTitleListParams({
      page,
      size,
      sort,
      direction,
      search,
      status,
    });
    return this.httpClient
      .get<ApiResponse<PageableData>>(this.resource(countryId), { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((it: any) => this.mapItem(it)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          } as RegimePageResponse;
        }),
      );
  }

  getRegimeById(countryId: string, id: string | number) {
    return this.httpClient
      .get<ApiResponse<any>>(this.resource(countryId, id))
      .pipe(map((resp) => this.mapItem(resp.data)));
  }

  createRegime(countryId: string, payload: any) {
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        this.resource(countryId),
        toCountryTitleApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Régimen creado' })));
  }

  updateRegime(countryId: string, id: string | number, payload: any) {
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        this.resource(countryId, id),
        toCountryTitleApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Régimen actualizado' })));
  }

  deleteRegime(countryId: string, id: string | number) {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(
        this.resource(countryId, id),
      )
      .pipe(map((res) => ({ message: res.message || 'Régimen eliminado' })));
  }
}

export interface Regime {
  id: string;
  name: string;
  relations?: number;
  status?: string;
  createdAt?: string;
}

export interface RegimePageResponse {
  items: Regime[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

